import type { LayoutItem } from 'react-grid-layout';
import {
  mergeVisibleLayout,
  parseDashboardLayout,
  serializeDashboardLayout,
} from '@/pages/dashboard/widgetCatalog';

/**
 * Browser copy of the Forside grid for one user in one clinic.
 * The server row in `user_dashboard_layouts` is the source of truth.
 * This key is only adopted when that row does not exist yet.
 */
export const DASHBOARD_LAYOUT_STORAGE_PREFIX = 'clinio_dashboard_layout';

export function dashboardLayoutStorageKey(userId: string, clinicId: string): string {
  return `${DASHBOARD_LAYOUT_STORAGE_PREFIX}:${userId}:${clinicId}`;
}

export type DashboardGridChangeSource =
  | 'drag-stop'
  | 'resize-stop'
  | 'add'
  | 'remove'
  | 'layout-change';

/**
 * react-grid-layout calls `onLayoutChange` when it mounts, compacts, and when
 * the layout prop changes — including the prop change from Nulstil. Saving
 * that echo writes the previous grid back over the add, remove, or reset.
 * Drag, resize, add, and remove are the only grid events that commit.
 */
export function shouldPersistDashboardGridChange(source: DashboardGridChangeSource): boolean {
  switch (source) {
    case 'drag-stop':
    case 'resize-stop':
    case 'add':
    case 'remove':
      return true;
    case 'layout-change':
      return false;
    default: {
      const exhaustive: never = source;
      return exhaustive;
    }
  }
}

export type DashboardLayoutDecision =
  | { use: 'server'; layout: LayoutItem[] }
  | { use: 'adopt-local'; layout: LayoutItem[] }
  | { use: 'default'; layout: null };

/**
 * Pick what to show and whether to copy a browser layout up to the server.
 * A server row always wins. A browser layout is adopted once, only when the
 * server has no row yet. After that the server row is the copy other browsers see.
 */
export function decideDashboardLayoutFetch(input: {
  server: LayoutItem[] | null;
  local: LayoutItem[] | null;
}): DashboardLayoutDecision {
  if (input.server) return { use: 'server', layout: input.server };
  if (input.local && input.local.length > 0) return { use: 'adopt-local', layout: input.local };
  return { use: 'default', layout: null };
}

/**
 * Nulstil restores the product default for widgets the user may see.
 * Widgets they cannot see stay in the saved row so they return if access does.
 * Widgets they added are not kept: the default replaces the visible grid.
 */
export function buildResetLayout(
  current: readonly LayoutItem[],
  defaults: readonly LayoutItem[],
  allowedIds: ReadonlySet<string>,
): LayoutItem[] {
  const visibleDefaults = defaults.filter((item) => allowedIds.has(item.i));
  return mergeVisibleLayout(current, visibleDefaults, allowedIds);
}

export function readLocalDashboardLayout(
  storage: Pick<Storage, 'getItem'>,
  userId: string,
  clinicId: string,
): LayoutItem[] | null {
  try {
    const raw = storage.getItem(dashboardLayoutStorageKey(userId, clinicId));
    if (!raw) return null;
    const parsed = parseDashboardLayout(JSON.parse(raw) as unknown);
    return parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

export function writeLocalDashboardLayout(
  storage: Pick<Storage, 'setItem'>,
  userId: string,
  clinicId: string,
  layout: readonly LayoutItem[],
): void {
  storage.setItem(
    dashboardLayoutStorageKey(userId, clinicId),
    JSON.stringify(serializeDashboardLayout(layout)),
  );
}

export function clearLocalDashboardLayout(
  storage: Pick<Storage, 'removeItem'>,
  userId: string,
  clinicId: string,
): void {
  storage.removeItem(dashboardLayoutStorageKey(userId, clinicId));
}
